export function callIfPossible<T>(prop: T, ...args: any[]): T;
export function callIfPossible<T>(fn: (...args: any[]) => T, ...args: any[]): T;
export function callIfPossible<T>(
  fn: (...args: any[]) => T,
  ...args: Parameters<typeof fn>
): T;
export function callIfPossible<T, Args extends any[]>(
  prop: T,
  ...args: Args
): any {
  if (typeof prop !== 'function') {
    return prop;
  }
  return prop(...args);
}

/**
 * Combines multiple event handlers into a single function.
 *
 * @example
 *  onClick={sequence(this.handleClick, onClick)}
 * @param  {...Function} handlers One or more event handlers to fire from the returned function
 * @returns {Function}
 */
export function sequence(
  ...handlers: Array<Function | undefined>
): (...args: any[]) => void {
  return (...args: any[]) => {
    handlers.forEach(
      (handler) => typeof handler === 'function' && handler(...args)
    );
  };
}

type SequenceWeakMap = WeakMap<Function | object, Function | SequenceWeakMap>;

let sequenceCache: SequenceWeakMap;
const resultKey: object = {};

/**
 * Combines multiple event handlers into a single function. Unlike `sequence`, `memoizedSequence`
 * uses a cache to ensure that the same function instance is returned every time that you call it
 * with the same arguments, preventing unnecessary React updates.
 *
 * @example
 *  onClick={memoizedSequence(this.handleClick, onClick)}
 * @param  {...Function} handlers One or more event handlers to fire from the returned function
 * @returns {Function}
 */
export function memoizedSequence(...handlers: Function[]): Function | void {
  const validHandlers = handlers.filter(
    (handler) => typeof handler === 'function'
  );
  if (validHandlers.length === 0) return undefined;
  if (validHandlers.length === 1) return validHandlers[0];

  // If this sequence has been generated before, return it
  let cache: SequenceWeakMap | Function | boolean | undefined = sequenceCache;
  validHandlers.forEach((handler) => {
    cache = cache && cache instanceof WeakMap && cache.get(handler);
  });
  if (cache instanceof WeakMap && cache.has(resultKey))
    return cache.get(resultKey) as Function;

  // Otherwise, generate a new sequence and save it in the cache
  const newSequence: Function = sequence(...validHandlers);
  if (sequenceCache === undefined) sequenceCache = new WeakMap();

  let newCache: SequenceWeakMap = sequenceCache;
  validHandlers.forEach((handler) => {
    if (!(newCache.get(handler) instanceof WeakMap)) {
      newCache.set(handler, new WeakMap());
    }
    newCache = newCache.get(handler) as SequenceWeakMap;
  });
  newCache.set(resultKey, newSequence);
  return newSequence;
}

interface RefToWrap {
  current: HTMLElement;
}
type WrappedRefFunction = (el: HTMLElement | HTMLInputElement) => void;
let wrapRefObjectCache: WeakMap<RefToWrap, WrappedRefFunction>;

const makeCallbackForRefObject = (ref: RefToWrap): WrappedRefFunction => {
  return (el: HTMLElement) => {
    ref.current = el;
  };
};

/**
 * Returns a callback wrapper around the given ref object.
 * @param {Object} ref
 */
export function wrapRefObject(
  ref: RefToWrap
): WrappedRefFunction | null | void {
  if (ref == null) return ref;

  if (wrapRefObjectCache === undefined) wrapRefObjectCache = new WeakMap();
  if (wrapRefObjectCache.has(ref)) return wrapRefObjectCache.get(ref)!;
  const callback = makeCallbackForRefObject(ref);
  wrapRefObjectCache.set(ref, callback);
  return callback;
}

export const isRenderable = (children: any): boolean =>
  children != null && children !== false;
